// react
import React, { useState } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { useMediaQuery, Box, Grid, Typography, TextField, Button, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// SafeTwin
import { generateMnemonic, mnemonicToEntropy } from "SafeTwin/crypto/bip39/bip39";
import { toHex, fromHex, toBase64, generateSeedEd25519KeyPair } from "SafeTwin/crypto/cryptolibsodium";

// A ---------------------------------------------------------------------- M

const KeyPairGenerator = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [privateKey, setPrivateKey] = useState("");
  const [publicKey, setPublicKey] = useState("");

  const handleGenerate = () => {
    // Generate mnemonic
    const mnemonic = generateMnemonic();

    // Derive entropy from mnemonic
    const seed = mnemonicToEntropy(mnemonic);

    // Generate keypair
    const keypair = generateSeedEd25519KeyPair(fromHex(seed));

    // Private key
    const privateKeyByteArray = keypair.sk;
    const privateKeyBase64 = toBase64(privateKeyByteArray);

    // Public key
    const publicKeyByteArray = keypair.pk;
    const publicKeyHex = toHex(publicKeyByteArray);

    setPrivateKey(privateKeyBase64);
    setPublicKey(publicKeyHex);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const buttonStyles = {
    py: isMobile ? 0.5 : 1,
    px: 3,
    borderRadius: 1,
    textTransform: "none",
    boxShadow: "none",
    borderColor: "divider",
    "&:hover": { backgroundColor: "action.hover", borderColor: "primary.main" },
  };

  return (
    <Box
      sx={{
        maxWidth: isMobile ? "100%" : 900,
        mx: "auto",
        mt: 8,
        p: isMobile ? 2 : 4,
        boxShadow: 1,
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
        {t("generate_keypair")}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        1. <b>{t("private_key")}</b>: {t("gen_key_2")}
        <br />
        2. <b>{t("public_key")}</b>: {t("gen_key_3")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={t("private_key")}
            value={privateKey}
            InputProps={{
              endAdornment: (
                <Tooltip title={t("copy")}>
                  <IconButton size="small" onClick={() => handleCopy(privateKey)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
            fullWidth
            sx={{ mb: 2, "& .MuiInputBase-root": { borderRadius: 1 } }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t("public_key")}
            value={publicKey}
            InputProps={{
              endAdornment: (
                <Tooltip title={t("copy")}>
                  <IconButton size="small" onClick={() => handleCopy(publicKey)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
            fullWidth
            sx={{ mb: 3, "& .MuiInputBase-root": { borderRadius: 1 } }}
          />
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button variant="outlined" onClick={handleGenerate} sx={buttonStyles}>
            {t("generate")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KeyPairGenerator;
